import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";
import { economyApi } from "../../../../api/economy";
import { blankPayrollRate, PAYROLL_RATE_DEFAULTS } from "./budgetModel";
import { Button, ErrorBox, Field, Select, useEconomyText, useRemote } from "./shared";

const payrollDefaults = { ...PAYROLL_RATE_DEFAULTS, employees: [] };
const vehicleDefaults = { financing: "owned", consumption_per_100km: "", energy_price: "", insurance_monthly: "", road_tax_monthly: "", loan_balance: "0", interest_percent: "0", remaining_months: "12", leasing_monthly: "0", leasing_km_year: "0", leasing_excess_per_km: "0" };
function hydratePayroll(stored) {
  const base = payrollDefaults;
  if (!stored) return { ...base, employees: [] };
  const next = { ...base, ...stored, employees: Array.isArray(stored.employees) ? stored.employees : [] };
  Object.keys(PAYROLL_RATE_DEFAULTS).forEach((key) => {
    if (blankPayrollRate(stored[key])) next[key] = PAYROLL_RATE_DEFAULTS[key];
  });
  return next;
}
export function Assumptions({ settingKey, employees = [], year, month, onSaved, onClose }) {
  const { t } = useEconomyText();
  const remote = useRemote((signal) => economyApi.settings(settingKey, signal), [settingKey]);
  const [draft, setDraft] = useState(null);
  const [version, setVersion] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [observed, setObserved] = useState(null);
  const period = `${year}-${String(month).padStart(2, "0")}`;
  const payroll = settingKey === "payroll";
  useEffect(() => {
    if (!remote.data || draft) return;
    const values = payroll ? hydratePayroll(remote.data.values) : (remote.data.values || vehicleDefaults);
    setDraft(payroll ? { ...values, employees: [...values.employees.filter((row) => !employees.some((employee) => String(employee.id) === String(row.id))), ...employees.map((employee) => values.employees.find((row) => String(row.id) === String(employee.id)) || { id: String(employee.id), rate: employee.salary_reference?.yearly_salary || employee.salary_reference?.hourly_rate || "", method: Number(employee.salary_reference?.yearly_salary) > 0 ? "annual" : "hourly", employment_percent: employee.salary_reference?.position_percentage ?? "100" })] } : values);
    setVersion(remote.data.lock_version);
  }, [remote.data, draft, employees, payroll]);
  const change = (key, value) => { setDraft((old) => ({ ...old, [key]: value })); setSaved(false); };
  const blocked = pending || error?.outcome === "unknown" || error?.httpStatus === 409;
  const save = async (event) => {
    event.preventDefault();
    if (blocked) return;
    setPending(true); setError(null); setObserved(null);
    let response;
    try { response = await economyApi.saveSettings(settingKey, { values: draft, lock_version: version }); }
    catch (failure) { setError(failure); }
    finally { setPending(false); }
    if (response) { setVersion(response.data.lock_version); setSaved(true); onSaved(); }
  };
  const check = async () => {
    setPending(true);
    try {
      const response = await economyApi.settings(settingKey);
      setObserved(response.data);
      if (isEqual(response.data.values, draft)) {
        setVersion(response.data.lock_version); setError(null); setSaved(true); onSaved();
      }
    } catch (failure) { setError(failure); }
    finally { setPending(false); }
  };
  if (remote.loading) return <p role="status">{t("loading")}</p>;
  if (remote.error) return <ErrorBox error={remote.error} onRetry={remote.refresh} />;
  if (!draft) return null;
  return <form className="eco-card" onSubmit={save}>
    <div className="eco-toolbar"><h3>{t("settings")}</h3><Button type="button" onClick={onClose} disabled={blocked}>{t("close")}</Button></div>
    <fieldset disabled={blocked}>
      <div className="eco-fields">
        {!payroll && <Select label={t("financing")} value={draft.financing} onChange={(value) => change("financing", value)} options={["owned", "loan", "leasing"].map((key) => [key, t(key)])} />}
        {Object.entries(draft).filter(([key]) => !["employees", "financing"].includes(key)).map(([key, value]) => <Field key={key} label={t(key)} value={value} onChange={(next) => change(key, next)} min="0" required />)}
      </div>
      {payroll && draft.employees.map((person, index) => {
        const employee = employees.find((row) => String(row.id) === String(person.id));
        if (!employee) return null;
        const update = (key, value) => change("employees", draft.employees.map((row, i) => i === index ? { ...row, [key]: value } : row));
        const absence = employee.months?.find((row) => row.month === month);
        const adjustment = person.adjustments?.[period] || { paid_hours: "0", deduction: "0", absence_token: null };
        const adjust = (key, value) => update("adjustments", { ...person.adjustments, [period]: { ...adjustment, [key]: value } });
        return <div className="eco-row-editor" key={person.id}><h3>{employee?.name}</h3><p className="eco-muted">{t("reference")}: {employee?.wage_reference || "—"} {employee?.wage_unit_reference}</p><div className="eco-fields">
          <Select label={t("method")} value={person.method} onChange={(value) => update("method", value)} options={["hourly", "monthly", "annual"].map((key) => [key, t(key)])} />
          <Field label={t("rate")} value={person.rate} onChange={(value) => update("rate", value)} min="0" required />
          <Field label={t("employment_percent")} value={person.employment_percent} onChange={(value) => update("employment_percent", value)} min="0" max="100" required />
        </div><h4>{t("absenceAdjustments")} · {period}</h4>
          {!!absence?.absence_count && <><p>{t("absenceReviewHelp")}</p><ul>{absence.absence_dates.map((row, i) => <li key={i}>{row.from} – {row.to} · {t(row.approved ? "approved" : "submitted")}</li>)}</ul></>}
          <div className="eco-fields">{person.method === "hourly" && <Field label={t("paid_absence_hours")} value={adjustment.paid_hours} min="0" max="744" onChange={(value) => adjust("paid_hours", value)} />}<Field label={t("absence_deduction")} value={adjustment.deduction} min="0" onChange={(value) => adjust("deduction", value)} /></div>
          {!!(absence?.absence_token || adjustment.absence_token) && <label><input type="checkbox" checked={adjustment.absence_token === absence.absence_token} onChange={(event) => adjust("absence_token", event.target.checked ? absence?.absence_token || null : null)} /> {t("absenceConfirmed")}</label>}
        </div>;
      })}
    </fieldset>
    <ErrorBox error={error} />
    {blocked && !pending && <Button type="button" onClick={check}>{t("checkStatus")}</Button>}
    {observed && !saved && <details><summary>{t("checkStatus")}</summary><p className="eco-muted">{t("conflict")}</p><Button type="button" onClick={() => { if (observed.values) setDraft(payroll ? hydratePayroll(observed.values) : observed.values); setVersion(observed.lock_version); setError(null); setObserved(null); }}>{t("useSaved")}</Button></details>}
    <div className="eco-toolbar"><Button className="eco-primary" disabled={blocked} type="submit">{pending ? t("saving") : t("save")}</Button>{saved && <p className="eco-success" role="status">{t("saved")}</p>}</div>
  </form>;
}
